import { Order } from "@swiggy-private/payment-types";
import { getBridgeSdk } from "@swiggy-private/checkout-core";
import { isEmpty, safeJsonParse, captureError } from "@swiggy-private/core-ui";

import { getDOPaymentSuccessData } from "./PaymentSuccessHelper";
import { CouponEventProps, IMetaData } from "@dineout-payment-success-ui/interfaces";
import { CLEVERTAP_KEYS, COUPON_TYPE, DealCategory } from "@dineout-payment-success-ui/constants/Constants";
import { IDiscount } from "@swiggy-private/checkout-types";

export const logSuccessPageViewedEvent = (orderData: Order): void => {
    if (isEmpty(orderData)) {
        return;
    }
    try {
        const paymentDetails = orderData.orderJobs[0]?.paymentInfo[0];
        const metaDataJson = safeJsonParse<IMetaData>(orderData.orderJobs[0].metadata);
        const discounts = metaDataJson?.bill?.discounts;
        const couponDiscount = getDiscountsForCleverTap(discounts);
        const confirmOrderInfo = getDOPaymentSuccessData(orderData.orderJobs[0].metadata);
        const clevertapParams = {
            paymentType: paymentDetails?.paymentMethodDisplayName || "",
            discountType: discounts?.map((item: IDiscount) => item?.type)?.join("") || "",
            [CLEVERTAP_KEYS.RESTAURANT_NAME]: confirmOrderInfo?.restaurantInfo?.restaurantName || "",
            [CLEVERTAP_KEYS.RID]: confirmOrderInfo?.restaurantId || "",
            [CLEVERTAP_KEYS.TOTAL_BILL_AMOUNT]: metaDataJson?.bill.totalBill,
            [CLEVERTAP_KEYS.COUPON_DISCOUNT]: couponDiscount.couponDiscount,
            [CLEVERTAP_KEYS.RESTAURANT_DISCOUNT]: couponDiscount.restaurantDiscount,
            [CLEVERTAP_KEYS.ONE_DISCOUNT]: couponDiscount.oneDiscount,
            [CLEVERTAP_KEYS.BANK_OFFER]: couponDiscount.bankOffer,
            [CLEVERTAP_KEYS.SUM_OF_ALL_DISCOUNT]: metaDataJson?.bill?.totalDiscountAmount,
        };
        getBridgeSdk().logClevertapEvent({ eventName: CLEVERTAP_KEYS.PAGE_VIEWED_EVENT, params: clevertapParams });
    } catch (error) {
        captureError(error);
    }
};

function getDiscountsForCleverTap(discounts?: IDiscount[]): CouponEventProps {
    const eventProps = {
        couponDiscount: "0",
        restaurantDiscount: "0",
        oneDiscount: "0",
        bankOffer: "0",
    };

    discounts?.forEach(item => {
        if (item?.couponType === COUPON_TYPE.PERCENT && item?.allianceIdentifier === null) {
            eventProps.couponDiscount = item?.value;
        }
        if (item?.couponType === COUPON_TYPE.PERCENT && item?.allianceIdentifier !== null) {
            eventProps.bankOffer = item?.value;
        }
        if (item?.type === COUPON_TYPE.STORE_DISCOUNT && item?.superOffer === false) {
            eventProps.restaurantDiscount = item?.value;
        }
        if (item?.type === COUPON_TYPE.STORE_DISCOUNT && item?.superOffer === true) {
            eventProps.oneDiscount = item?.value;
        }
    });
    return eventProps;
}

export const logTableReservationShareEvent = (bookingId: string, restaurantId: string, guestCount: number): void => {
    try {
        getBridgeSdk().logClevertapEvent({
            eventName: CLEVERTAP_KEYS.BOOKED_DEAL_SHARE_CLICK,
            params: {
                bookingId,
                restaurantId,
                guestCount,
            },
        });
    } catch (error) {
        captureError(error);
    }
};

interface IDealDataForEvent {
    amount?: string;
    offerLogo?: {
        text?: string;
    };
    guestCount?: number;
    restaurantId?: string;
    restaurantInfo?: {
        name?: string;
        locality?: string;
    };
    bookingStatusEventName?: string;
}

interface IDealPurchaseEventParams {
    orderId: string;
    dealDataForEvent: IDealDataForEvent;
    dealCategory: DealCategory;
    epochTimeOfBookedSlot?: number;
    cxReadableDate?: string;
    dealDetailsDeeplink?: string;
}

export const logDealPurchaseEvent = ({
    orderId,
    dealDataForEvent,
    dealCategory,
    epochTimeOfBookedSlot,
    cxReadableDate,
    dealDetailsDeeplink,
}: IDealPurchaseEventParams): void => {
    try {
        const commonEventProps = {
            customerPaidAmount: dealDataForEvent?.amount ?? "0",
            dealTitle: dealDataForEvent?.offerLogo?.text ?? "",
            coverCount: dealDataForEvent?.guestCount?.toString() ?? "",
            rxID: dealDataForEvent?.restaurantId ?? "",
            rxName: dealDataForEvent?.restaurantInfo?.name ?? "",
            rxLocality: dealDataForEvent?.restaurantInfo?.locality ?? "",
        };

        if (dealCategory === DealCategory.RESERVATION) {
            const eventProps = {
                bookingId: orderId,
                bookingSlot: epochTimeOfBookedSlot?.toString() ?? "",
                cxReadableDate,
                bookingDetailScreenLink: dealDetailsDeeplink,
            };

            getBridgeSdk().logClevertapEvent({
                eventName: CLEVERTAP_KEYS.DINEOUT_BOOKING_CONFIRM,
                params: {
                    ...commonEventProps,
                    ...eventProps,
                },
            });
        } else if (dealCategory === DealCategory.GIRF) {
            const eventProps = {
                dealPurchaseOrderId: orderId,
                dealBookingSlot: epochTimeOfBookedSlot?.toString() ?? "",
                girfDeal: "true",
            };

            getBridgeSdk().logClevertapEvent({
                eventName: CLEVERTAP_KEYS.DINEOUT_GIRF_DEAL_PURCHASE,
                params: {
                    ...commonEventProps,
                    ...eventProps,
                },
            });
        }
    } catch (error) {
        captureError(error);
    }
};
